import {
  buildStoryTeamBattleRole,
  STORY_BATTLE_ROLE_SOURCE,
} from "./storyHumanCharacterCards";
import { resolveActiveStoryCompanion } from "./storyCompanions";
import { resolveStoryBattleTeam } from "./storyTeamRules";
import {
  WHISPERING_FOREST_PORTAL_RETURN_SPAWN,
} from "./voidCityStoryPortalSpawns";

export const STORY_SIDE_QUEST_GATE_MISSION_ID = "act-2-mission-3";
export const STORY_SIDE_QUEST_SET_ID = "act-2-questmaster-clears-1";
export const STORY_SIDE_QUEST_REQUIRED_LEVEL = 15;
export const STORY_SIDE_QUEST_REQUIRED_TURN_INS = 3;
export const STORY_SIDE_QUEST_ENCOUNTER_MODE = "roamingCreatureClear";
export const STORY_SIDE_QUEST_COUNTER_LABEL = "Creatures Remaining";
export const STORY_SIDE_QUEST_TOTAL_ASSIGNMENTS = 16;
export const STORY_SIDE_QUEST_STATUS = Object.freeze({
  AVAILABLE: "available",
  ACTIVE: "active",
  WORLD_COMPLETE: "world_complete",
  TURNED_IN: "turned_in",
});

const QUEST_MASTER_PORTRAIT =
  "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/profile_images%2Fquest-master.png?alt=media&token=fa8977b5-5d52-4f26-8682-b9720cdf4568";

const normalizeKey = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9:-]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeIdList = (value = []) =>
  [...new Set((Array.isArray(value) ? value : []).map(normalizeKey).filter(Boolean))];

const buildQuestMasterSpeaker = () => ({
  name: "Quest Master",
  portrait: QUEST_MASTER_PORTRAIT,
});

const buildQuestReminderScript = (quest) => [
  {
    speaker: buildQuestMasterSpeaker(),
    text: `Finish clearing ${quest.location} before you come back for another assignment.`,
  },
  {
    speaker: buildQuestMasterSpeaker(),
    text: "Bring me results, not excuses.",
  },
];

const buildQuestTurnInScript = (quest) => [
  {
    speaker: buildQuestMasterSpeaker(),
    text: `Well done. ${quest.shortAreaName} is calmer now, and the Guild has already confirmed the corruption is thinning.`,
  },
  {
    speaker: buildQuestMasterSpeaker(),
    text: "Take a moment to recover, then speak to me again if you're ready for another assignment.",
  },
];

const makeEncounter = ({
  encounterId,
  leadCreatureId,
  creatureIds,
  x,
  y,
  width = 120,
  height = 96,
  aggroRange = 136,
  giveUpRange = 246,
  touchRange = 36,
  roamSpeed = 0.34,
  chaseSpeed = 1.28,
  returnPoint = null,
}) => ({
  encounterId,
  leadCreatureId,
  creatureIds,
  home: { x, y },
  homeBounds: { width, height },
  aggroRange,
  giveUpRange,
  touchRange,
  roamSpeed,
  chaseSpeed,
  returnTarget: {
    x: Number(returnPoint?.x ?? x),
    y: Number(returnPoint?.y ?? y),
  },
});

const CITY_FOREST_PORTAL_WORLD = Object.freeze({
  mapId: "voidCity",
  building: "",
  spawnId: "whispering-forest-portal",
  x: WHISPERING_FOREST_PORTAL_RETURN_SPAWN.x,
  y: WHISPERING_FOREST_PORTAL_RETURN_SPAWN.y,
});

const WHISPERING_FOREST_WORLD = Object.freeze({
  mapId: "whisperingForest",
  building: "",
  spawnId: "whispering-forest-entry",
  x: 512,
  y: 880,
});

const STORY_SIDE_QUESTS = Object.freeze([
  {
    id: "qm-clear-city-forest-road",
    title: "Forest Road Sweep",
    subtitle: "Void City Approach Clearance",
    objective:
      "Clear the corrupted creatures threatening the road between Void City and the Whispering Forest portal.",
    location: "Void City Forest Road",
    shortAreaName: "the forest road approach",
    world: CITY_FOREST_PORTAL_WORLD,
    sceneKey: "VoidCity2Scene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-city-forest-road",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-city-road-1",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "geode"],
        x: 1100,
        y: 1220,
        width: 82,
        height: 44,
      }),
      makeEncounter({
        encounterId: "sq-city-road-2",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "sprout"],
        x: 1270,
        y: 1220,
        width: 82,
        height: 44,
      }),
      makeEncounter({
        encounterId: "sq-city-road-3",
        leadCreatureId: "geode",
        creatureIds: ["geode", "geode", "sprout"],
        x: 1420,
        y: 1220,
        width: 82,
        height: 44,
      }),
    ],
  },
  {
    id: "qm-clear-city-grove",
    title: "City Grove Purge",
    subtitle: "Park Corruption Cleanup",
    objective:
      "Sweep the park grove outside Void City before the corruption spreads deeper into the city edge.",
    location: "Void City Grove",
    shortAreaName: "the city grove",
    world: CITY_FOREST_PORTAL_WORLD,
    sceneKey: "VoidCity2Scene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-city-grove",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-city-grove-1",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "shroom"],
        x: 1200,
        y: 1440,
        width: 80,
        height: 64,
      }),
      makeEncounter({
        encounterId: "sq-city-grove-2",
        leadCreatureId: "geode",
        creatureIds: ["geode", "sprout", "shroom"],
        x: 1360,
        y: 1480,
        width: 80,
        height: 64,
      }),
      makeEncounter({
        encounterId: "sq-city-grove-3",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "shroom"],
        x: 1510,
        y: 1450,
        width: 72,
        height: 60,
      }),
    ],
  },
  {
    id: "qm-clear-city-crosswalk",
    title: "Crosswalk Sweep",
    subtitle: "South Gate Pressure Relief",
    objective:
      "Break up the corruption packs clustering around the south-side crosswalk before they spread back toward the Arena road.",
    location: "Void City South Crosswalk",
    shortAreaName: "the south crosswalk",
    world: CITY_FOREST_PORTAL_WORLD,
    sceneKey: "VoidCity2Scene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-city-crosswalk",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-city-crosswalk-1",
        leadCreatureId: "geode",
        creatureIds: ["geode", "sprout"],
        x: 760,
        y: 1688,
        width: 96,
        height: 48,
      }),
      makeEncounter({
        encounterId: "sq-city-crosswalk-2",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "sprout", "shroom"],
        x: 880,
        y: 1688,
        width: 96,
        height: 48,
      }),
      makeEncounter({
        encounterId: "sq-city-crosswalk-3",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "geode", "sprout"],
        x: 1000,
        y: 1760,
        width: 72,
        height: 56,
      }),
    ],
  },
  {
    id: "qm-clear-city-garden-line",
    title: "Garden Line Sweep",
    subtitle: "Fence Row Cleanup",
    objective:
      "Clear the creatures working their way along the garden fence before the corruption reaches deeper into the city pathing.",
    location: "Void City Garden Line",
    shortAreaName: "the garden fence line",
    world: CITY_FOREST_PORTAL_WORLD,
    sceneKey: "VoidCity2Scene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-city-garden-line",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-city-garden-line-1",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "sprout", "shroom"],
        x: 480,
        y: 640,
        width: 80,
        height: 44,
      }),
      makeEncounter({
        encounterId: "sq-city-garden-line-2",
        leadCreatureId: "geode",
        creatureIds: ["geode", "shroom"],
        x: 720,
        y: 640,
        width: 80,
        height: 44,
      }),
      makeEncounter({
        encounterId: "sq-city-garden-line-3",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "geode", "sprout"],
        x: 1024,
        y: 640,
        width: 80,
        height: 44,
      }),
    ],
  },
  {
    id: "qm-clear-city-grove-rim",
    title: "Grove Rim Purge",
    subtitle: "Outer Park Containment",
    objective:
      "Contain the corruption circling the outer edge of the grove before it spills back toward the nearby roads.",
    location: "Void City Grove Rim",
    shortAreaName: "the grove rim",
    world: CITY_FOREST_PORTAL_WORLD,
    sceneKey: "VoidCity2Scene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-city-grove-rim",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-city-grove-rim-1",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "sprout"],
        x: 1120,
        y: 1328,
        width: 64,
        height: 64,
      }),
      makeEncounter({
        encounterId: "sq-city-grove-rim-2",
        leadCreatureId: "geode",
        creatureIds: ["geode", "geode", "sprout"],
        x: 1500,
        y: 1330,
        width: 64,
        height: 60,
      }),
      makeEncounter({
        encounterId: "sq-city-grove-rim-3",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "shroom", "sprout"],
        x: 1120,
        y: 1540,
        width: 64,
        height: 64,
      }),
    ],
  },
  {
    id: "qm-clear-city-forest-verge",
    title: "Forest Verge Sweep",
    subtitle: "Portal Tree Line Cleanup",
    objective:
      "Push the corruption back from the tree line around the forest portal before the route becomes unstable again.",
    location: "Void City Forest Verge",
    shortAreaName: "the portal tree line",
    world: CITY_FOREST_PORTAL_WORLD,
    sceneKey: "VoidCity2Scene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-city-forest-verge",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-city-forest-verge-1",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "geode"],
        x: 1320,
        y: 1580,
        width: 72,
        height: 60,
      }),
      makeEncounter({
        encounterId: "sq-city-forest-verge-2",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "sprout", "geode"],
        x: 1450,
        y: 1580,
        width: 72,
        height: 60,
      }),
      makeEncounter({
        encounterId: "sq-city-forest-verge-3",
        leadCreatureId: "geode",
        creatureIds: ["geode", "shroom"],
        x: 1400,
        y: 1830,
        width: 72,
        height: 60,
      }),
    ],
  },
  {
    id: "qm-clear-forest-entry",
    title: "Forest Entry Sweep",
    subtitle: "Whispering Forest Frontline",
    objective:
      "Eliminate the corrupt creatures gathering near the entrance to the Whispering Forest.",
    location: "Whispering Forest Entry",
    shortAreaName: "the forest entrance",
    world: WHISPERING_FOREST_WORLD,
    sceneKey: "WhisperingForestScene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-forest-entry",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-forest-entry-1",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "sprout"],
        x: 540,
        y: 856,
        width: 132,
        height: 100,
      }),
      makeEncounter({
        encounterId: "sq-forest-entry-2",
        leadCreatureId: "geode",
        creatureIds: ["geode", "shroom"],
        x: 668,
        y: 780,
        width: 148,
        height: 116,
      }),
      makeEncounter({
        encounterId: "sq-forest-entry-3",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "sprout", "geode"],
        x: 788,
        y: 714,
        width: 166,
        height: 124,
      }),
    ],
  },
  {
    id: "qm-clear-forest-lower-thicket",
    title: "Lower Thicket Sweep",
    subtitle: "Forest Brushline Cleanup",
    objective:
      "Drive the corruption out of the lower thicket before it can regroup around the forest entrance.",
    location: "Whispering Forest Lower Thicket",
    shortAreaName: "the lower thicket",
    world: WHISPERING_FOREST_WORLD,
    sceneKey: "WhisperingForestScene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-forest-lower-thicket",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-forest-lower-thicket-1",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "shroom"],
        x: 404,
        y: 950,
        width: 128,
        height: 104,
      }),
      makeEncounter({
        encounterId: "sq-forest-lower-thicket-2",
        leadCreatureId: "geode",
        creatureIds: ["geode", "sprout", "geode"],
        x: 612,
        y: 932,
        width: 156,
        height: 118,
      }),
      makeEncounter({
        encounterId: "sq-forest-lower-thicket-3",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "sprout"],
        x: 760,
        y: 904,
        width: 126,
        height: 98,
      }),
    ],
  },
  {
    id: "qm-clear-forest-central",
    title: "Forest Heart Sweep",
    subtitle: "Central Grove Clearance",
    objective:
      "Push through the central grove and clear the corruption gathering around the deeper forest path.",
    location: "Whispering Forest Central Grove",
    shortAreaName: "the central grove",
    world: WHISPERING_FOREST_WORLD,
    sceneKey: "WhisperingForestScene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-forest-central",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-forest-central-1",
        leadCreatureId: "geode",
        creatureIds: ["geode", "shroom"],
        x: 706,
        y: 520,
        width: 140,
        height: 108,
      }),
      makeEncounter({
        encounterId: "sq-forest-central-2",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "geode", "sprout"],
        x: 844,
        y: 462,
        width: 164,
        height: 118,
      }),
      makeEncounter({
        encounterId: "sq-forest-central-3",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "shroom", "geode"],
        x: 920,
        y: 602,
        width: 164,
        height: 122,
      }),
    ],
  },
  {
    id: "qm-clear-forest-west-glen",
    title: "West Glen Purge",
    subtitle: "Quiet Path Containment",
    objective:
      "Sweep the western glen and keep the corruption from circling back onto the safer forest paths.",
    location: "Whispering Forest West Glen",
    shortAreaName: "the western glen",
    world: WHISPERING_FOREST_WORLD,
    sceneKey: "WhisperingForestScene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-forest-west-glen",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-forest-west-glen-1",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "geode"],
        x: 438,
        y: 632,
        width: 120,
        height: 104,
      }),
      makeEncounter({
        encounterId: "sq-forest-west-glen-2",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "sprout", "shroom"],
        x: 548,
        y: 548,
        width: 150,
        height: 124,
      }),
      makeEncounter({
        encounterId: "sq-forest-west-glen-3",
        leadCreatureId: "geode",
        creatureIds: ["geode", "shroom", "sprout"],
        x: 604,
        y: 438,
        width: 148,
        height: 112,
      }),
    ],
  },
  {
    id: "qm-clear-forest-east-branch",
    title: "East Branch Sweep",
    subtitle: "Branch Trail Stabilization",
    objective:
      "Clear the eastern branch trail and stop the void-corrupt creatures from overrunning the split path.",
    location: "Whispering Forest East Branch",
    shortAreaName: "the eastern branch trail",
    world: WHISPERING_FOREST_WORLD,
    sceneKey: "WhisperingForestScene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-forest-east-branch",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-forest-east-branch-1",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "shroom"],
        x: 986,
        y: 690,
        width: 126,
        height: 106,
      }),
      makeEncounter({
        encounterId: "sq-forest-east-branch-2",
        leadCreatureId: "geode",
        creatureIds: ["geode", "sprout", "geode"],
        x: 1062,
        y: 572,
        width: 150,
        height: 124,
      }),
      makeEncounter({
        encounterId: "sq-forest-east-branch-3",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "sprout", "shroom"],
        x: 1014,
        y: 438,
        width: 146,
        height: 118,
      }),
    ],
  },
  {
    id: "qm-clear-forest-upper-trail",
    title: "Upper Trail Purge",
    subtitle: "Void Cave Approach Clearance",
    objective:
      "Secure the upper trail beneath the Void Cave gate before the corruption seals the route completely.",
    location: "Whispering Forest Upper Trail",
    shortAreaName: "the upper trail",
    world: WHISPERING_FOREST_WORLD,
    sceneKey: "WhisperingForestScene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-forest-upper-trail",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-forest-upper-1",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "shroom"],
        x: 446,
        y: 286,
        width: 132,
        height: 116,
      }),
      makeEncounter({
        encounterId: "sq-forest-upper-2",
        leadCreatureId: "geode",
        creatureIds: ["geode", "sprout", "shroom"],
        x: 560,
        y: 228,
        width: 148,
        height: 126,
      }),
      makeEncounter({
        encounterId: "sq-forest-upper-3",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "geode"],
        x: 690,
        y: 182,
        width: 124,
        height: 100,
      }),
    ],
  },
  {
    id: "qm-clear-forest-cave-foothills",
    title: "Cave Foothills Sweep",
    subtitle: "Final Forest Approach",
    objective:
      "Clear the foothills below Void Cave so the route stays navigable when the Guild finally grants passage.",
    location: "Whispering Forest Cave Foothills",
    shortAreaName: "the cave foothills",
    world: WHISPERING_FOREST_WORLD,
    sceneKey: "WhisperingForestScene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-forest-cave-foothills",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-forest-cave-foothills-1",
        leadCreatureId: "geode",
        creatureIds: ["geode", "sprout"],
        x: 372,
        y: 216,
        width: 124,
        height: 104,
      }),
      makeEncounter({
        encounterId: "sq-forest-cave-foothills-2",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "geode", "sprout"],
        x: 522,
        y: 174,
        width: 150,
        height: 116,
      }),
      makeEncounter({
        encounterId: "sq-forest-cave-foothills-3",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "sprout", "geode"],
        x: 676,
        y: 146,
        width: 150,
        height: 112,
      }),
    ],
  },
  {
    id: "qm-clear-forest-ridgewatch",
    title: "Ridgewatch Purge",
    subtitle: "High Path Cleanup",
    objective:
      "Secure the high ridge path and keep the corruption from taking hold over the caveward approach.",
    location: "Whispering Forest Ridgewatch",
    shortAreaName: "the high ridge path",
    world: WHISPERING_FOREST_WORLD,
    sceneKey: "WhisperingForestScene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-forest-ridgewatch",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-forest-ridgewatch-1",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "shroom"],
        x: 834,
        y: 262,
        width: 128,
        height: 104,
      }),
      makeEncounter({
        encounterId: "sq-forest-ridgewatch-2",
        leadCreatureId: "geode",
        creatureIds: ["geode", "geode", "shroom"],
        x: 930,
        y: 198,
        width: 152,
        height: 118,
      }),
      makeEncounter({
        encounterId: "sq-forest-ridgewatch-3",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "sprout", "geode"],
        x: 1032,
        y: 142,
        width: 154,
        height: 114,
      }),
    ],
  },
  {
    id: "qm-clear-forest-backtrail",
    title: "Backtrail Sweep",
    subtitle: "Rear Path Stabilization",
    objective:
      "Purge the rear forest trail and keep the corruption from encircling the main route from behind.",
    location: "Whispering Forest Backtrail",
    shortAreaName: "the backtrail",
    world: WHISPERING_FOREST_WORLD,
    sceneKey: "WhisperingForestScene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-forest-backtrail",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-forest-backtrail-1",
        leadCreatureId: "geode",
        creatureIds: ["geode", "sprout"],
        x: 872,
        y: 844,
        width: 132,
        height: 106,
      }),
      makeEncounter({
        encounterId: "sq-forest-backtrail-2",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "shroom", "sprout"],
        x: 978,
        y: 786,
        width: 156,
        height: 118,
      }),
      makeEncounter({
        encounterId: "sq-forest-backtrail-3",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "geode", "sprout"],
        x: 1092,
        y: 720,
        width: 154,
        height: 120,
      }),
    ],
  },
  {
    id: "qm-clear-forest-deep-approach",
    title: "Deep Approach Sweep",
    subtitle: "Inner Trail Pressure Break",
    objective:
      "Break the densest corruption knots along the inner forest approach and keep the deeper route from collapsing.",
    location: "Whispering Forest Deep Approach",
    shortAreaName: "the deep approach",
    world: WHISPERING_FOREST_WORLD,
    sceneKey: "WhisperingForestScene",
    encounterMode: STORY_SIDE_QUEST_ENCOUNTER_MODE,
    encounterSetId: "sq-forest-deep-approach",
    counterLabel: STORY_SIDE_QUEST_COUNTER_LABEL,
    reminderScript: [],
    turnInScript: [],
    encounters: [
      makeEncounter({
        encounterId: "sq-forest-deep-approach-1",
        leadCreatureId: "shroom",
        creatureIds: ["shroom", "geode"],
        x: 736,
        y: 356,
        width: 122,
        height: 102,
      }),
      makeEncounter({
        encounterId: "sq-forest-deep-approach-2",
        leadCreatureId: "sprout",
        creatureIds: ["sprout", "sprout", "geode"],
        x: 860,
        y: 324,
        width: 148,
        height: 114,
      }),
      makeEncounter({
        encounterId: "sq-forest-deep-approach-3",
        leadCreatureId: "geode",
        creatureIds: ["geode", "shroom", "sprout"],
        x: 964,
        y: 302,
        width: 154,
        height: 118,
      }),
    ],
  },
]).map((quest) =>
  Object.freeze({
    ...quest,
    reminderScript: buildQuestReminderScript(quest),
    turnInScript: buildQuestTurnInScript(quest),
  }),
);

const STORY_SIDE_QUESTS_BY_ID = new Map(
  STORY_SIDE_QUESTS.map((quest) => [quest.id, quest]),
);

const STORY_SIDE_QUESTS_BY_ENCOUNTER_SET_ID = new Map(
  STORY_SIDE_QUESTS.map((quest) => [quest.encounterSetId, quest]),
);

const getEncounterCount = (encounter = null) =>
  Array.isArray(encounter?.creatureIds) ? encounter.creatureIds.length : 0;

const getTotalCreatureCount = (quest = null) =>
  (quest?.encounters || []).reduce(
    (total, encounter) => total + getEncounterCount(encounter),
    0,
  );

const buildDefaultSideQuestProgress = (quest = null) => ({
  status: STORY_SIDE_QUEST_STATUS.AVAILABLE,
  defeatedEncounterIds: [],
  defeatedCreatureCount: 0,
  totalCreatureCount: getTotalCreatureCount(quest),
  lastTriggeredEncounterId: null,
});

export const normalizeStorySideQuestProgressEntry = (
  questId,
  rawEntry = null,
) => {
  const quest = STORY_SIDE_QUESTS_BY_ID.get(normalizeKey(questId)) || null;
  const defaultEntry = buildDefaultSideQuestProgress(quest);
  const defeatedEncounterIds = normalizeIdList(rawEntry?.defeatedEncounterIds).filter(
    (encounterId) =>
      (quest?.encounters || []).some(
        (encounter) => normalizeKey(encounter.encounterId) === encounterId,
      ),
  );
  const totalCreatureCount = Math.max(
    defaultEntry.totalCreatureCount,
    Math.floor(Number(rawEntry?.totalCreatureCount || defaultEntry.totalCreatureCount)),
  );
  const defeatedCreatureCount = Math.max(
    0,
    Math.min(
      totalCreatureCount,
      Math.floor(Number(rawEntry?.defeatedCreatureCount || 0)),
    ),
  );
  const status = Object.values(STORY_SIDE_QUEST_STATUS).includes(rawEntry?.status)
    ? rawEntry.status
    : defaultEntry.status;

  return {
    status,
    defeatedEncounterIds,
    defeatedCreatureCount,
    totalCreatureCount,
    lastTriggeredEncounterId: normalizeKey(rawEntry?.lastTriggeredEncounterId) || null,
  };
};

export const normalizeStorySideQuestProgressMap = (rawProgress = {}) =>
  Object.entries(rawProgress || {}).reduce((acc, [rawQuestId, rawEntry]) => {
    const questId = normalizeKey(rawQuestId);
    if (!questId || !STORY_SIDE_QUESTS_BY_ID.has(questId)) return acc;
    acc[questId] = normalizeStorySideQuestProgressEntry(questId, rawEntry);
    return acc;
  }, {});

export const normalizeTurnedInStorySideQuestIds = (value = []) =>
  normalizeIdList(value).filter((questId) => STORY_SIDE_QUESTS_BY_ID.has(questId));

export const getStorySideQuestById = (questId = "") =>
  STORY_SIDE_QUESTS_BY_ID.get(normalizeKey(questId)) || null;

export const getStorySideQuestByEncounterSetId = (encounterSetId = "") =>
  STORY_SIDE_QUESTS_BY_ENCOUNTER_SET_ID.get(normalizeKey(encounterSetId)) || null;

export const getStorySideQuestEncounter = (encounterSetId = "", encounterId = "") => {
  const quest = getStorySideQuestByEncounterSetId(encounterSetId);
  if (!quest) return null;
  return (
    quest.encounters.find(
      (encounter) => normalizeKey(encounter.encounterId) === normalizeKey(encounterId),
    ) || null
  );
};

export const getStoryQuestMasterSideQuests = () => STORY_SIDE_QUESTS;

export const resolveStorySideQuestProgress = (storyState = null, questId = "") => {
  const normalizedQuestId = normalizeKey(questId);
  const quest = getStorySideQuestById(normalizedQuestId);
  if (!quest) return null;

  const turnedInSet = new Set(
    normalizeTurnedInStorySideQuestIds(storyState?.turnedInStorySideQuestIds),
  );
  const rawProgress = storyState?.storySideQuestProgress?.[normalizedQuestId];
  const normalized = normalizeStorySideQuestProgressEntry(normalizedQuestId, rawProgress);
  const creaturesRemaining = Math.max(
    0,
    normalized.totalCreatureCount - normalized.defeatedCreatureCount,
  );

  let status = normalized.status;
  if (turnedInSet.has(normalizedQuestId)) {
    status = STORY_SIDE_QUEST_STATUS.TURNED_IN;
  } else if (
    creaturesRemaining <= 0 ||
    normalized.defeatedEncounterIds.length >= quest.encounters.length
  ) {
    status = STORY_SIDE_QUEST_STATUS.WORLD_COMPLETE;
  } else if (
    normalizeKey(storyState?.activeStorySideQuestId) === normalizedQuestId
  ) {
    status = STORY_SIDE_QUEST_STATUS.ACTIVE;
  }

  return {
    ...normalized,
    status,
    quest,
    creaturesRemaining,
  };
};

export const getActiveStorySideQuest = (storyState = null) => {
  const activeQuestId = normalizeKey(storyState?.activeStorySideQuestId);
  if (!activeQuestId) return null;
  const progress = resolveStorySideQuestProgress(storyState, activeQuestId);
  return progress?.quest ? progress : null;
};

export const getTurnedInStorySideQuestCount = (storyState = null) =>
  normalizeTurnedInStorySideQuestIds(storyState?.turnedInStorySideQuestIds).length;

export const getAvailableStorySideQuests = (storyState = null) => {
  const activeQuestId = normalizeKey(storyState?.activeStorySideQuestId);
  const turnedInSet = new Set(
    normalizeTurnedInStorySideQuestIds(storyState?.turnedInStorySideQuestIds),
  );

  return STORY_SIDE_QUESTS.filter((quest) => {
    const questId = normalizeKey(quest.id);
    if (turnedInSet.has(questId)) return false;
    if (activeQuestId && activeQuestId === questId) return false;
    return true;
  }).map((quest) => ({
    ...quest,
    progress: resolveStorySideQuestProgress(storyState, quest.id),
  }));
};

export const isStorySideQuestApprovalReady = (
  storyState = null,
  {
    requiredStoryLevel = STORY_SIDE_QUEST_REQUIRED_LEVEL,
    requiredTurnIns = STORY_SIDE_QUEST_REQUIRED_TURN_INS,
  } = {},
) =>
  Number(storyState?.level || 0) >= Number(requiredStoryLevel || 0) &&
  getTurnedInStorySideQuestCount(storyState) >= Number(requiredTurnIns || 0);

export const buildStorySideQuestObjectiveData = (mission = {}, storyState = null) => {
  const activeQuestProgress = getActiveStorySideQuest(storyState);
  const turnedInCount = getTurnedInStorySideQuestCount(storyState);
  const requiredTurnIns = Number(
    mission?.requiredTurnIns || STORY_SIDE_QUEST_REQUIRED_TURN_INS,
  );
  const requiredStoryLevel = Number(
    mission?.requiredStoryLevel || STORY_SIDE_QUEST_REQUIRED_LEVEL,
  );
  const approvalReady = isStorySideQuestApprovalReady(storyState, {
    requiredStoryLevel,
    requiredTurnIns,
  });

  if (activeQuestProgress?.status === STORY_SIDE_QUEST_STATUS.WORLD_COMPLETE) {
    return {
      title: activeQuestProgress.quest.title,
      objective: "Return to the Quest Master to confirm completion.",
      location: "Quest Hall",
      rewards: [
        `${turnedInCount}/${requiredTurnIns} turn-ins`,
        `Story level ${Number(storyState?.level || 1)}/${requiredStoryLevel}`,
      ],
      targetTypeLabel: "Turn In",
    };
  }

  if (activeQuestProgress) {
    return {
      title: activeQuestProgress.quest.title,
      objective: activeQuestProgress.quest.objective,
      location: activeQuestProgress.quest.location,
      rewards: [
        `${Math.max(0, activeQuestProgress.creaturesRemaining)} creatures remaining`,
        `${turnedInCount}/${requiredTurnIns} turn-ins`,
        `Story level ${Number(storyState?.level || 1)}/${requiredStoryLevel}`,
      ],
      targetTypeLabel: "Area Clearing",
    };
  }

  if (approvalReady) {
    return {
      title: mission?.missionTitle || "Side Quest Clearance",
      objective:
        "Speak to the Quest Master for Void Cave clearance, or keep taking optional side quests to grow beyond Story level 15.",
      location: mission?.location || "Quest Hall",
      rewards: [`Void Cave access`, `Glyde joins the Story Team`],
      targetTypeLabel: "Approval Ready",
    };
  }

  if (!activeQuestProgress) {
    return {
      title: mission?.missionTitle || "Side Quest Clearance",
      objective: `Speak to the Quest Master to accept a side quest. Turn in ${requiredTurnIns} quests and reach Story level ${requiredStoryLevel}.`,
      location: mission?.location || "Quest Hall",
      rewards: [
        `${turnedInCount}/${requiredTurnIns} turn-ins`,
        `Story level ${Number(storyState?.level || 1)}/${requiredStoryLevel}`,
        `${STORY_SIDE_QUESTS.length} total assignments available`,
      ],
      targetTypeLabel: "Assignments",
    };
  }

  return {
    title: mission?.missionTitle || "Side Quest Clearance",
    objective: `Speak to the Quest Master to accept a side quest. Turn in ${requiredTurnIns} quests and reach Story level ${requiredStoryLevel}.`,
    location: mission?.location || "Quest Hall",
    rewards: [
      `${turnedInCount}/${requiredTurnIns} turn-ins`,
      `Story level ${Number(storyState?.level || 1)}/${requiredStoryLevel}`,
      `${STORY_SIDE_QUESTS.length} total assignments available`,
    ],
    targetTypeLabel: "Assignments",
  };
};

export const buildStorySideQuestVictoryProgress = ({
  storyState = null,
  questId = "",
  encounterId = "",
} = {}) => {
  const progress = resolveStorySideQuestProgress(storyState, questId);
  const encounter = progress?.quest?.encounters?.find(
    (entry) => normalizeKey(entry.encounterId) === normalizeKey(encounterId),
  );
  if (!progress || !encounter) return progress;
  if (progress.defeatedEncounterIds.includes(normalizeKey(encounter.encounterId))) {
    return progress;
  }

  const defeatedEncounterIds = [
    ...progress.defeatedEncounterIds,
    normalizeKey(encounter.encounterId),
  ];
  const defeatedCreatureCount = Math.max(
    0,
    Math.min(
      progress.totalCreatureCount,
      progress.defeatedCreatureCount + getEncounterCount(encounter),
    ),
  );
  const creaturesRemaining = Math.max(
    0,
    progress.totalCreatureCount - defeatedCreatureCount,
  );

  return {
    ...progress,
    status:
      creaturesRemaining <= 0
        ? STORY_SIDE_QUEST_STATUS.WORLD_COMPLETE
        : STORY_SIDE_QUEST_STATUS.ACTIVE,
    defeatedEncounterIds,
    defeatedCreatureCount,
    creaturesRemaining,
    lastTriggeredEncounterId: normalizeKey(encounter.encounterId),
  };
};

export const buildStorySideQuestEncounterBattle = ({
  encounterSetId = "",
  encounterId = "",
  storyState = null,
} = {}) => {
  const quest = getStorySideQuestByEncounterSetId(encounterSetId);
  const encounter = getStorySideQuestEncounter(encounterSetId, encounterId);
  if (!quest || !encounter) return null;

  const { leaderKey, teammateKeys } = resolveStoryBattleTeam(storyState);
  const activeCompanion = resolveActiveStoryCompanion(storyState);

  return {
    launchMode: "direct_match",
    encounterId: encounter.encounterId,
    sideQuestId: quest.id,
    mode: "teams",
    storySide: {
      sideId: "red",
      leaderRole: buildStoryTeamBattleRole(leaderKey),
      teammateRoles: teammateKeys.map(buildStoryTeamBattleRole),
    },
    opponentSide: {
      sideId: "blue",
      opponentRoles: encounter.creatureIds.map((creatureId, index) => ({
        source: STORY_BATTLE_ROLE_SOURCE.STORY_CREATURE,
        characterId: creatureId,
        alias:
          encounter.creatureIds.length > 1
            ? `${creatureId}-${index + 1}`
            : creatureId,
      })),
    },
    roomConfig: {
      amountOfPlayers:
        1 + teammateKeys.length + Math.max(1, encounter.creatureIds.length),
      botFill: false,
      public: false,
      ranked: false,
      timedTurns: true,
      turnTime: 30,
      mode: "teams",
      teamCount: 2,
      teamOrder: ["red", "blue"],
    },
    completionPolicy: {
      defaultResultKey: "completed_unknown",
      onResults: {},
    },
    returnTarget: {
      ...quest.world,
      spawnId: encounter.encounterId,
      x: encounter.returnTarget.x,
      y: encounter.returnTarget.y,
    },
    resumePolicy: {
      allowResumeActiveMatch: true,
      restartIfRoomMissing: true,
    },
    storyCompanionAura: activeCompanion
      ? {
          companionId: activeCompanion.id,
        }
      : null,
  };
};

export const buildStorySideQuestRuntime = (storyState = null, questId = "") => {
  const progress = resolveStorySideQuestProgress(storyState, questId);
  if (!progress?.quest) return null;

  return {
    questId: progress.quest.id,
    missionId: STORY_SIDE_QUEST_GATE_MISSION_ID,
    sceneKey: progress.quest.sceneKey,
    encounterSetId: progress.quest.encounterSetId,
    encounterMode: progress.quest.encounterMode,
    counterLabel: progress.quest.counterLabel,
    title: progress.quest.title,
    objective: progress.quest.objective,
    world: progress.quest.world,
    defeatedEncounterIds: progress.defeatedEncounterIds,
    defeatedCreatureCount: progress.defeatedCreatureCount,
    totalCreatureCount: progress.totalCreatureCount,
    creaturesRemaining: progress.creaturesRemaining,
    lastTriggeredEncounterId: progress.lastTriggeredEncounterId,
    status: progress.status,
    encounters: progress.quest.encounters,
    blockVoidCave: true,
  };
};
